import { db } from '../firebase';
import { doc, runTransaction, serverTimestamp } from 'firebase/firestore';
import {
  receiptDocument,
  receiptNumberDocument,
  receiptNumberKey,
  receiptsCollection,
} from '../lib/paths';

// Re-exported: the key derivation lives with the other paths, but it has
// always been part of this module's API
export { receiptNumberKey };

/**
 * Atomic receipt-number uniqueness via a reservation collection.
 *
 * Every receipt number is reserved in
 * `users/{uid}/receiptNumbers/{numberKey}` inside the same transaction that
 * writes the receipt, so two tabs, devices
 * or syncing offline clients can never commit the same number — the second
 * transaction sees the reservation and either bumps to the next free number
 * or fails with DuplicateReceiptNumberError.
 *
 * Receipts created before this mechanism have no reservation docs; the
 * existing query-based duplicate pre-checks remain the guard for those.
 */

// Thrown when the requested number is taken and auto-resolution is not
// possible (e.g. a manually chosen number that doesn't follow the
// NN/DD/MM/YYYY pattern)
export class DuplicateReceiptNumberError extends Error {
  constructor(number: string) {
    super(`Receipt number already exists: ${number}`);
    this.name = 'DuplicateReceiptNumberError';
  }
}

const NUMBER_PATTERN = /^(\d+)\/(\d{2})\/(\d{2})\/(\d{4})$/;

// Returns the next number in the NN/DD/MM/YYYY sequence, or null when the
// number doesn't follow the generated pattern and cannot be bumped
export const bumpReceiptNumber = (number: string): string | null => {
  const match = number.match(NUMBER_PATTERN);
  if (!match) return null;
  const next = parseInt(match[1], 10) + 1;
  const padded = next.toString().padStart(Math.max(2, match[1].length), '0');
  return `${padded}/${match[2]}/${match[3]}/${match[4]}`;
};

export const numberForDate = (date: Date, sequence: number): string => {
  const day = date.getDate().toString().padStart(2, '0');
  const month = (date.getMonth() + 1).toString().padStart(2, '0');
  const year = date.getFullYear().toString();
  return `${sequence.toString().padStart(2, '0')}/${day}/${month}/${year}`;
};

// Upper bound on candidate probes within one transaction — far above any
// realistic single-day receipt volume for one account
const MAX_PROBES = 50;

interface CreateOptions {
  // When the requested number is taken and not bumpable, fall back to a
  // date-derived NN/DD/MM/YYYY sequence instead of throwing. Used by the
  // offline sync path, which must not lose receipts over a number clash.
  fallbackDate?: Date;
}

interface CreateResult {
  id: string;
  number: string;
}

/**
 * Create a receipt and reserve its number atomically. Returns the created
 * doc ID and the final number (which may differ from the requested one if
 * it was taken and had to be bumped).
 */
export const createReceiptWithUniqueNumber = async (
  userUID: string,
  receiptDocData: Record<string, unknown> & { number: string },
  options: CreateOptions = {}
): Promise<CreateResult> => {
  return runTransaction(db, async transaction => {
    let candidate: string = receiptDocData.number;
    let usingFallback = false;
    let fallbackSequence = 1;
    let reservationRef = receiptNumberDocument(userUID, candidate);

    for (let probe = 0; probe < MAX_PROBES; probe++) {
      const existing = await transaction.get(reservationRef);
      if (!existing.exists()) {
        const receiptRef = doc(receiptsCollection(userUID));
        transaction.set(reservationRef, {
          number: candidate,
          numberKey: receiptNumberKey(candidate),
          receiptId: receiptRef.id,
          createdAt: serverTimestamp(),
        });
        transaction.set(receiptRef, { ...receiptDocData, number: candidate });
        return { id: receiptRef.id, number: candidate };
      }

      // Candidate taken — pick the next one to probe
      const bumped = usingFallback ? null : bumpReceiptNumber(candidate);
      if (bumped) {
        candidate = bumped;
      } else if (options.fallbackDate) {
        usingFallback = true;
        candidate = numberForDate(options.fallbackDate, fallbackSequence++);
      } else {
        throw new DuplicateReceiptNumberError(candidate);
      }
      reservationRef = receiptNumberDocument(userUID, candidate);
    }

    throw new DuplicateReceiptNumberError(candidate);
  });
};

/**
 * Update a receipt, moving its number reservation when the number changed.
 * Throws DuplicateReceiptNumberError if the new number is already reserved
 * for a different receipt — an edited number is the user's explicit choice,
 * so it is never silently bumped.
 */
export const updateReceiptWithUniqueNumber = async (
  userUID: string,
  receiptId: string,
  receiptDocData: Record<string, unknown> & { number: string }
): Promise<void> => {
  const receiptRef = receiptDocument(userUID, receiptId);
  const newNumber = receiptDocData.number;
  const newReservationRef = receiptNumberDocument(userUID, newNumber);

  await runTransaction(db, async transaction => {
    const receiptSnap = await transaction.get(receiptRef);
    const oldNumber = receiptSnap.exists()
      ? (receiptSnap.data().number as string | undefined)
      : undefined;

    if (oldNumber !== newNumber) {
      const existing = await transaction.get(newReservationRef);
      if (existing.exists() && existing.data().receiptId !== receiptId) {
        throw new DuplicateReceiptNumberError(newNumber);
      }

      // All reads must precede writes, so resolve the old reservation now.
      // Receipts that predate the reservation mechanism may still have none
      // — only delete what exists and actually belongs to this receipt
      let oldReservationRef = null;
      if (oldNumber) {
        const candidateRef = receiptNumberDocument(userUID, oldNumber);
        const oldSnap = await transaction.get(candidateRef);
        if (oldSnap.exists()) {
          const reservedFor = oldSnap.data().receiptId as string | undefined;
          if (!reservedFor || reservedFor === receiptId) {
            oldReservationRef = candidateRef;
          }
        }
      }

      // A set on an existing doc counts as an update, which the rules
      // deny — skip it when the reservation already points at this receipt
      if (!existing.exists()) {
        transaction.set(newReservationRef, {
          number: newNumber,
          numberKey: receiptNumberKey(newNumber),
          receiptId,
          createdAt: serverTimestamp(),
        });
      }

      if (oldReservationRef) {
        transaction.delete(oldReservationRef);
      }
    }

    transaction.update(receiptRef, receiptDocData);
  });
};

/**
 * Release a receipt's number reservation after the receipt is deleted.
 * Only removes the reservation if it points at the deleted receipt (or
 * predates receipt tracking), so an unrelated receipt's number is never
 * freed by mistake.
 */
export const releaseReceiptNumber = async (
  userUID: string,
  number: string,
  receiptId?: string
): Promise<void> => {
  const reservationRef = receiptNumberDocument(userUID, number);

  await runTransaction(db, async transaction => {
    const snap = await transaction.get(reservationRef);
    if (!snap.exists()) return;
    const reservedFor = snap.data().receiptId as string | undefined;
    if (!receiptId || !reservedFor || reservedFor === receiptId) {
      transaction.delete(reservationRef);
    }
  });
};
